import PCLNAnalytics from '@pcln/analytics'
import { mark } from '@pcln/measure'
import { stripIgnoredCharacters } from 'graphql'
import get from 'lodash/get'

import pclnRequest from '@pcln/request'
import { APOLLO_CLIENT_NAME } from '../../../common/constants/constants'
import { PERF_EVENTS } from '../../../common/constants/perfEvents'
import {
  getIsChildPricingEligible,
  getIsInfantPricingEligible,
} from '../../../common/multiPaxPricingEligibility'
import { getGraphURL } from '../../../utils/getServiceUrl'
import { appendGqlOp } from '../../../../common/utils/appendGqlOp'
import { clientErrorRedirect } from '../../helpers/clientErrorRedirect'
import { validateSearchParams } from '../../helpers/helpers'
import { logSearchError } from '../../util/clientLogger'
import { getExperimentHeaders } from '../../util/getExperimentHeaders/getExperimentHeaders'
import { isServerSide } from '../../util/isServerSide'
import { buildMap } from '../../util/listingsHelpers'
import { buildSearchRequest } from './airSearchRequest'
import { airSearchRespQuery } from './queries/airSearchRespQuery'

const airSearch = ({
  pclnBootstrapData,
  fromFilters = false, // request originating from filters path
  fromSlice = false,
  fromLoadMore = false, // request originating from loadmore path
  sortOptions,
  airSearchRequestModel,
  filterDefaults = [],
  cguid,
  incomingReq,
  streamingSSR,
  filterInfo,
  logger = {},
  rguid,
  activeTabId,
  listingsMetaData,
  numOfRenderedListings,
  employeeDiscountEnabled = false, // NEW: Employee discount toggle state
  standardSessionKey = '', // NEW: Standard pricing session key
  employeeSessionKey = '', // NEW: Employee pricing session key
}) => {
  if (!isServerSide) {
    mark(PERF_EVENTS.buildSearchRequestStart)
  }

  if (!validateSearchParams(pclnBootstrapData?.searchRequest?.params ?? {})) {
    clientErrorRedirect()
  }

  /**
   * NOTE:
   * There are multiple ways of checking variant in this file
   * getIsChildPricingEligible(), getIsInfantPricingEligible getListingsTestState
   * all grabs data from pclnBootstrapData, except:
   * 1 is passed as prop, 2 & 3 is grabbing from window object
   *
   * Needs refactoring
   */
  const AIR_ENABLE_CHILD_PRICING = getIsChildPricingEligible({ pclnBootstrapData })
  const enableLapInfants = getIsInfantPricingEligible({ pclnBootstrapData })
  const requestObject = buildSearchRequest({
    fromFilters,
    fromSlice,
    pclnBootstrapData,
    sortOptions,
    AIR_ENABLE_CHILD_PRICING,
    enableLapInfants,
    airSearchRequestModel,
    filterDefaults,
    filterInfo,
    fromLoadMore,
    rguid,
    activeTabId,
    listingsMetaData,
    numOfRenderedListings,
    employeeDiscountEnabled, // Pass employee discount parameters
    standardSessionKey,
    employeeSessionKey,
  })

  const data = {
    operationName: 'airSearchResp',
    variables: {
      input: {
        ...requestObject,
        searchOptions: {
          ...requestObject.searchOptions,
          upsell: requestObject.searchOptions.upsell,
        },
      },
    },
    // strip ignored characters, including consecutive whitespace, to reduce request body size
    query: stripIgnoredCharacters(airSearchRespQuery),
  }

  const requestType = 'Graph Schema Request Type'
  if (!isServerSide) {
    mark(PERF_EVENTS.buildSearchRequestEnd)
  }

  const appVersion = get(pclnBootstrapData, 'appVersion')
  const skipPclnGraph = pclnBootstrapData?.features?.flights?.skipPclnGraph
  let headers = {
    'apollographql-client-name': `${APOLLO_CLIENT_NAME}`,
    'apollographql-client-version': `${appVersion}`,
    'x-pcln-search-source': 'appname:mflysearch-pagename:listing-searchtype:ugs',
    'workflow-id': pclnBootstrapData?.workflowId,
    cguid,
    'x-pcln-cguid': cguid,
  }

  // force USP to skip cache and get fresh listings from providers
  if (pclnBootstrapData?.searchRequest?.params?.refreshSupply === 'true') {
    headers['x-pcln-cached'] = 'false'
  }

  const experimentHeaders = getExperimentHeaders(pclnBootstrapData)

  headers = { ...headers, ...experimentHeaders }

  // Fastly sets headers 'authorization' on incomingReq by reading cookies, it also unsets the cookie
  // See implementation here: https://github.com/pcln/fastly/pull/1606/files
  // As local development doesn't have fastly, we need read incomingReq?.cookies?.pclnauthn as a fallback
  if (incomingReq?.headers?.authorization || incomingReq?.cookies?.pclnauthn) {
    // TODO: Remove the manual addition of the authorization header once
    // pclnRequest module takes care of forwarding the authorization headers set inside incomingReq,
    // it has to take care of case with local development where fastly is not present.
    headers['authorization'] = incomingReq?.headers?.authorization || incomingReq?.cookies?.pclnauthn || ''
  }

  return new Promise((resolve, reject) => {
    if (!isServerSide) {
      mark(PERF_EVENTS.AirSearchSchemaReq)
    }

    const airSearchUrl = streamingSSR
      ? appendGqlOp({ endpoint: process.env.PCLN_GRAPH_CALL, gqlOp: data.operationName })
      : getGraphURL({
          skipPclnGraph,
          useLocalFlyql: pclnBootstrapData.useLocalFlyql,
          operationName: data.operationName,
        })
    const dataAndHeaders = { data, headers }

    const requestlogObject = {
      action: 'REACT_STREAMING',
      label: 'AIR_SEARCH_REQUEST',
      request: JSON.stringify(requestObject),
      searchType: requestObject.offerToken ? 'EXCHANGE_REQUEST' : requestObject.requestType,
    }
    if (logger.info) {
      logger.info({ ...requestlogObject })
    } else {
      PCLNAnalytics.log({
        category: 'Flight Listings',
        ...requestlogObject,
      })
    }

    const startInitialSearchSSR = Date.now()
    const timeout = pclnBootstrapData?.isReshopExperience
      ? pclnBootstrapData?.features?.flights?.rebookPathTimeout
      : 30000

    pclnRequest(
      {
        url: airSearchUrl,
        method: 'post',
        timeout: timeout,
        ...dataAndHeaders,
      },
      // Passing incomingReq to pclnRequest to ensure it forwards all x-pcln headers
      // and in the future it would also take care of forwarding other headers such as authorization, cguid etc.
      incomingReq
    )
      .then((searchResult) => {
        const duration = Date.now() - startInitialSearchSSR
        if (!isServerSide) {
          const label = fromFilters ? 'filtersCall' : fromLoadMore ? 'loadMoreCall' : 'initialAirSearch'
          PCLNAnalytics.log({
            category: 'Flight Listings',
            action: 'search call duration',
            label,
            requestId: requestObject.requestId,
            appCode: requestObject.appCode,
            duration,
          })

          mark(PERF_EVENTS.successGraphAirSearchRsp)
          mark(PERF_EVENTS.transformGraphAirSearchRspStart)
        }

        if (logger?.info) {
          logger.info({
            requestId: requestObject.requestId,
            appCode: requestObject.appCode,
            label: 'Inital Search SSR',
            duration,
          })
        }
        let response = {}

        const { airSearchResp } = searchResult?.data?.data || {}
        response = airSearchResp || {}

        // ex. ZERO ITINS RETURNED
        if (response.error) {
          if (logger?.error) {
            logger.error({
              action: 'REACT_STREAMING',
              label: 'AIR_SEARCH_ERROR',
              requestType,
              requestId: rguid,
              err: response.error,
              cguid,
            })
          }
          reject(response.error?.message || 'No Error object provided')
        }

        const airlinesArray = response?.airline ?? []
        const airportsArray = response?.airport ?? []
        const airlines = buildMap(airlinesArray)
        const airports = buildMap(airportsArray)

        airSearchRequestModel.setParams({ response })
        response.airlines = airlines
        response.airports = airports

        resolve({
          request: requestObject.airSearchReq || requestObject.airSearchDisplayReq,
          response,
        })
      })
      .catch((error) => {
        if (logger?.error) {
          logger.error({
            action: 'REACT_STREAMING',
            label: 'AIR_SEARCH_ERROR',
            requestType,
            requestId: rguid,
            err: error,
            cguid,
          })
        }
        logSearchError(requestType, error)
        reject(error)
      })
  })
}

export { airSearch }
